import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { BlogPostMeta } from '../../data/blogPosts';

/* ──────────────────────────────────────────────────────────────────────────
   BlogCoverCard — tarjeta de portada a foto completa + degradado inferior,
   compartida entre BlogPage (listado) y BlogPostPage (relacionados), para que
   ambos se vean exactamente iguales sin duplicar ~80 líneas de estilo entre
   los dos archivos. Requiere que la página que lo use importe
   `pages/missionControl.css` (ahí viven las clases .blog-cover-card*).
   ────────────────────────────────────────────────────────────────────────── */

interface BlogCoverCardProps {
  post: BlogPostMeta;
  variant?: 'featured' | 'grid';
  onClick: () => void;
  delay?: number;
}

export const BlogCoverCard: React.FC<BlogCoverCardProps> = ({ post, variant = 'grid', onClick, delay = 0 }) => {
  const { t } = useTranslation();
  const isFeatured = variant === 'featured';

  const title =
    // El destacado ('featured') es el único post cuyo título viene partido en
    // dos claves (…_pre + …_grad, para pintar el cierre con degradado) — el
    // resto sí tiene una clave `_title` única.
    post.key === 'featured' ? (
      <>
        {t('blog_page.featured_title_pre')}
        <span className="grad-text">{t('blog_page.featured_title_grad')}</span>
      </>
    ) : (
      t(`blog_page.${post.key}_title`)
    );

  return (
    <motion.article
      initial={{ opacity: 0, y: isFeatured ? 24 : 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: isFeatured ? -3 : -4 }}
      onClick={onClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      className="blog-cover-card"
      style={{
        position: 'relative',
        minHeight: isFeatured ? 480 : 380,
        borderRadius: isFeatured ? 22 : 18,
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      <div
        aria-hidden
        className="blog-cover-card-bg"
        style={{ position: 'absolute', inset: 0, backgroundImage: `url(${post.image})`, backgroundSize: 'cover', backgroundPosition: post.imagePosition ?? 'center' }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0,
          background: isFeatured
            ? 'rgba(6,6,7,.88)'
            : 'linear-gradient(180deg, rgba(6,6,7,.97) 0%, rgba(6,6,7,.9) 40%, rgba(6,6,7,.18) 70%, transparent 85%)',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, padding: isFeatured ? 'clamp(2.25rem, 4vw, 3rem) clamp(2.25rem, 4vw, 3rem) 0' : '1.75rem 1.75rem 0' }}>
        <span
          style={{
            display: 'inline-flex', fontSize: isFeatured ? '.7rem' : '.68rem', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
            padding: isFeatured ? '.35rem .85rem' : '.3rem .75rem', borderRadius: 999,
            border: '1px solid transparent',
            color: '#fff',
            background: '#0F5132',
            marginBottom: isFeatured ? '1.1rem' : '1rem',
          }}
        >
          {t(`blog_page.${post.key}_tag`)}
        </span>
        {isFeatured ? (
          <h2 style={{ color: '#fff', fontSize: 'clamp(1.9rem, 3.6vw, 2.5rem)', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em', margin: '0 0 .9rem', maxWidth: '30rem' }}>
            {title}
          </h2>
        ) : (
          <h3 style={{ color: '#fff', fontSize: '1.32rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em', margin: '0 0 .7rem' }}>
            {title}
          </h3>
        )}
        <p
          style={{
            color: '#c9c9cc', fontSize: isFeatured ? '1.02rem' : '.9rem', lineHeight: isFeatured ? 1.65 : 1.6, maxWidth: isFeatured ? '32rem' : undefined,
            margin: '0 0 1rem',
            ...(isFeatured ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }),
          }}
        >
          {t(`blog_page.${post.key}_excerpt`)}
        </p>
        <span style={{ color: 'rgba(255,255,255,.75)', fontSize: isFeatured ? '.82rem' : '.78rem' }}>
          {t('blog_post.author', 'Equipo RurayHUB')} · {isFeatured && <>{t(`blog_page.${post.key}_date`)} · </>}
          {t(`blog_page.${post.key}_read`)} {t('blog_page.read_time_suffix')}
        </span>
      </div>
      <span
        aria-hidden
        className="blog-chevron-btn"
        style={{ position: 'absolute', right: isFeatured ? '1.75rem' : '1.25rem', bottom: isFeatured ? '1.75rem' : '1.25rem', width: isFeatured ? 40 : 34, height: isFeatured ? 40 : 34 }}
      >
        <ChevronIcon small={!isFeatured} />
      </span>
    </motion.article>
  );
};

// Trazo blanco en reposo (el botón es vidrio translúcido sobre foto oscura —
// negro ahí era ilegible); .blog-chevron-btn:hover lo pasa a negro cuando el
// fondo del botón se vuelve blanco sólido (ver missionControl.css).
const ChevronIcon: React.FC<{ small?: boolean }> = ({ small }) => (
  <svg width={small ? 13 : 15} height={small ? 13 : 15} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default BlogCoverCard;
